import { AmplifyCore, caseUtils } from '@codametrix/ui-common';
import { connect } from 'react-redux';
import type { CodingScreenState, WorkspaceMode } from '../../project_starburst';
import { CodingScreen } from './coding-screen';
import {
  codingStateFromCaseRun,
  gradingStateFromCodeBenchCase
} from './stateMappers/caseRunToCodingScreenState';

// The same production mapping getDetail's reducer uses (cases.reducer.ts:731):
// pick the primary CaseRun out of the full CMxAPI.Case.
const { getCaseRun } = caseUtils;

/**
 * Which sibling-case source feeds the Encounter popover's "Concurrent cases" list, mirroring the
 * legacy Related tab's own branch (`code-benchv2.tsx`): IPC service lines get the encounter-scoped
 * `relatedCases` list (`state.codeBench.relatedCases`, populated by `getCasesWithEncounter` --
 * `coding-screen.tsx`'s own dispatch); everything else gets the billing-group `groupedCases`
 * (`state.cases.groupedCases`, already populated as a side effect of `getDetail`'s internal
 * `getBillingGroup` dispatch whenever the case has a `billing_group_id` -- no new fetch needed).
 * `relatedCases` is passed through unfiltered: the legacy Related tab (`related-cases.tsx`) renders
 * the active case too (just flagged `isCurrent`), so this list should track the API's own count
 * one-for-one rather than silently dropping an entry. `groupedCases` is a different source that's
 * already exclude-active-case filtered upstream in the reducer (`cases.reducer.ts` via
 * `caseUtils.getGroupedCases` -> `excludeActiveCaseRun`) -- untouched here.
 */
function siblingCaseRunsForRun(
  run: CMxAPI.CaseRun,
  groupedCases: CMxCommonApp.GroupedCases | undefined,
  relatedCases: readonly CMxCommonApp.RelatedCaseExtended[] | undefined
): readonly CMxAPI.CaseRun[] | readonly CMxCommonApp.RelatedCaseExtended[] | undefined {
  if (AmplifyCore.isIPCServiceline(run.service_line)) {
    return relatedCases;
  }
  return groupedCases?.caseRuns;
}

type CodingScreenOwnProps = {
  mode?: WorkspaceMode;
  match?: { params?: Record<string, string | undefined> };
};

/**
 * Manual last-value memo. connect re-runs mapState on every redux
 * dispatch; rebuilding the mapped `CodingScreenState` (and its nested
 * `PatientInfoData`) each tick would hand connect a fresh object ref every time,
 * defeating its shallow-equal and re-rendering every slot consumer plus
 * re-allocating the provider value. Keyed on (mode, sourceRef, notesRef): while BOTH source redux
 * objects stay referentially stable the identical mapped-state ref is returned, so connect skips
 * the re-render. `notesRef` is `undefined` for grading (that source is never read),
 * so grading's memo key is unaffected by this addition.
 */
let memoMode: WorkspaceMode | undefined;
let memoSource: unknown;
let memoNotes: unknown;
let memoNotesCount: number | undefined;
let memoActivityCount: number | undefined;
let memoWorkItem: unknown;
let memoGroupedCases: unknown;
let memoRelatedCases: unknown;
let memoState: CodingScreenState | undefined;

function mapSourceToCodingScreenState(
  mode: WorkspaceMode | undefined,
  source: unknown,
  notes: readonly CMxCommonApp.DecoratedNote[] | undefined,
  notesCount: number,
  activityCount: number,
  workItem: CMxCommonApp.WorkItem | undefined,
  groupedCases: CMxCommonApp.GroupedCases | undefined,
  relatedCases: readonly CMxCommonApp.RelatedCaseExtended[] | undefined
): CodingScreenState | undefined {
  if (
    mode === memoMode &&
    source === memoSource &&
    notes === memoNotes &&
    notesCount === memoNotesCount &&
    activityCount === memoActivityCount &&
    workItem === memoWorkItem &&
    groupedCases === memoGroupedCases &&
    relatedCases === memoRelatedCases
  ) {
    return memoState;
  }
  memoMode = mode;
  memoSource = source;
  memoNotes = notes;
  memoNotesCount = notesCount;
  memoActivityCount = activityCount;
  memoWorkItem = workItem;
  memoGroupedCases = groupedCases;
  memoRelatedCases = relatedCases;

  if (source == null) {
    memoState = undefined;
  } else if (mode === 'grading') {
    // Grading source is `state.codeBench.activeItem` — the CQA case record the existing grade-bench
    // renders from (loadCQAItemFromURL populates it). Declared `CMxAPI.CaseRun`, but really a
    // snake_case CQA case, so `gradingStateFromCodeBenchCase` reads it (account # off the flat
    // `hospital_account_record`). Guard the empty {} initial shell → undefined (nothing until loaded).
    // Grading is out of scope for note display -- `notes` is never read on this branch.
    const activeItem = source as CMxAPI.CaseRun;
    memoState =
      Object.keys(activeItem).length > 0
        ? gradingStateFromCodeBenchCase(activeItem, activityCount, workItem)
        : undefined;
  } else {
    // Coding source is the raw full CMxAPI.Case (cases.reducer.ts:729). Guard on a
    // non-empty caseRuns first: getCaseRun throws 'no case runs available' otherwise,
    // and mapState runs on every tick — including before the case has loaded.
    const caseDetail = source as CMxAPI.Case;
    const run = caseDetail.caseRuns?.length ? getCaseRun(caseDetail) : undefined;
    memoState = run
      ? codingStateFromCaseRun(
          run,
          notes ?? [],
          notesCount,
          activityCount,
          workItem,
          siblingCaseRunsForRun(run, groupedCases, relatedCases)
        )
      : undefined;
  }
  return memoState;
}

/**
 * Maps redux → the props `CodingScreen` forwards into the widened shell, for BOTH modes. Each mode's
 * fetch (dispatched by `CodingScreen` on mount) populates redux but returns no value used here, so the
 * shell state is read back from the store:
 *   coding  → `getDetail` → `state.cases.apiResponse.activeItem` (full CMxAPI.Case, cases.reducer.ts:729)
 *   grading → `loadCQAItemFromURL` → `state.codeBench.activeItem` (the CQA case record the existing
 *             grade-bench itself renders from). `match` (route params) flows through from ownProps via
 *             connect's default merge to `CodingScreen`, which owns the fetch.
 *
 * CODING ONLY: `state.codeBench.notes` is the SAME `GET_HTML/fulfilled`-populated slice
 * the existing grade-bench Notes experience reads (`code-bench.reducer.ts`) -- `CodingScreen`'s own
 * `getDocumentsAsHtml` dispatch (coding mode only) populates it regardless of which route dispatched
 * it, since the reducer is keyed on the action type, not the route. Read unconditionally here (its
 * value is simply unused on the grading branch, mirroring `mapSourceToCodingScreenState`'s own
 * mode-gating) rather than duplicating the `mode === 'grading'` branch a second time.
 */
export function mapStateToCodingScreenProps(
  state: CMx.ApplicationState,
  ownProps: CodingScreenOwnProps
): { mode?: WorkspaceMode; state?: CodingScreenState } {
  const mode = ownProps.mode;
  const source =
    mode === 'grading'
      ? state.codeBench?.activeItem
      : state.cases?.apiResponse?.activeItem;
  // The documents-as-HTML slice the document pane renders (coding-only in practice; simply unused on
  // the grading branch).
  const notes = state.codeBench?.notes;
  // Coder-note count for the Notes badge/tab (coding-only). Guarded to 0 so the memo key stays a
  // stable number.
  const notesCount =
    mode === 'grading'
      ? 0
      : (state.coderNotes?.coderNotesList?.length ?? 0);
  // Activity count for the Activity tab pill. Each mode's activity lands on a different slice
  // (coding → codeBench.activity via fetchActivity; grading → gradeBenchPanel.activity via
  // getGraderWorkItemActivity); neither is on the declared slice type, so both go through a narrow cast.
  const activityCount =
    mode === 'grading'
      ? ((state.gradeBenchPanel as unknown as { activity?: unknown[] })?.activity
          ?.length ?? 0)
      : ((state.codeBench as unknown as { activity?: unknown[] })?.activity
          ?.length ?? 0);
  const workItem = state.cases?.workItem;
  const groupedCases = mode === 'grading' ? undefined : state.cases?.groupedCases;
  const relatedCases = mode === 'grading' ? undefined : state.codeBench?.relatedCases;

  return {
    mode,
    state: mapSourceToCodingScreenState(
      mode,
      source,
      notes,
      notesCount,
      activityCount,
      workItem,
      groupedCases,
      relatedCases
    )
  };
}

export const ConnectedCodingScreen = connect(mapStateToCodingScreenProps)(
  CodingScreen
);
